import type { ComponentProps, FC, ReactNode } from 'react';
import React, { useContext } from 'react';
import { styled } from '@compiled/react';
import { useQuery } from '@apollo/react-hooks';
import { token } from '@atlaskit/tokens';

import { expVal } from '@confluence/feature-experiments';
import { usePageContentId } from '@confluence/page-context';
import { LoadingPriority } from '@confluence/loadable';
import { useCreateSpaceExperiment } from '@confluence/create-space/entry-points/createSpace';
import { createSingleQueryParamHook } from '@confluence/route-manager/entry-points/RouteState';
import { InlineCommentsLoader } from '@confluence/inline-comments/entry-points/InlineCommentsLoader';
import { QuickReload, PageMode } from '@confluence/quick-reload';
import { useSessionData } from '@confluence/session-data';
import { ShareFeedbackButtonLoader } from '@confluence/company-hub-utils/entry-points/ShareFeedbackButtonLoader';
import { CompanyHubGroupedButtons } from '@confluence/company-hub/entry-points/CompanyHubGroupedButtons';
import {
	useViewPageParams,
	ViewPageContainer,
	ViewPageContent,
} from '@confluence/view-page-common';
import {
	DocumentUpdateStatusProvider,
	AnnotationsProvider,
} from '@confluence/annotation-provider-store';
import { RendererActionsProvider } from '@confluence/renderer-actions';
import {
	getRendererCoverPictureInfo,
	RendererPageCoverPicture,
} from '@confluence/page-cover-picture';
import { ContentViewedEvent, ContentRefetchProvider } from '@confluence/content-body';
import { InlineCommentsHighlighterLoader } from '@confluence/inline-comments/entry-points/InlineCommentsHighlighterLoader';
import { getEmojiFromPublishedPageProperties } from '@confluence/emoji-title/entry-points/transformer';
import { InlineCommentsContext } from '@confluence/comment-context';
import {
	useTitleContentPropertiesForPublishedPage,
	COVERPICTUREWIDTH,
} from '@confluence/custom-sites-extensions';
import { ReadingAidsAcronymsHighlighter } from '@confluence/contextual-reading-aids/entry-points/readingAidsAcronymsHighlighter';
import { SpaceOverviewQuery } from '@confluence/space-overview-query';
import { isCompanyHubSpaceKey } from '@confluence/route-manager/entry-points/companyHubUtils';
import {
	CUSTOM_HEADER_FOOTER_TYPES,
	CustomHeaderFooterLoader,
} from '@confluence/custom-header-footer';

import { SpaceOverviewGroupedButtons } from './SpaceOverviewGroupedButtons';
import { StartSpaceOverviewPageLoad } from './perf.config';
import { SpaceHeading } from './SpaceHeader';

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const ContentBodyStyledComponent = styled.div<{
	isCompanyHub: boolean;
}>({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
	'&:global': {
		/* Fixes padding for overview tab in non fallback mode. It relies on a global selector to avoid breaking theming */
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
		'.wiki-content': {
			padding: '28px 0px',
		},
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
		iframe: {
			marginTop: token('space.negative.250', '-20px'),
		},
	},
	paddingTop: 0,
	paddingRight: `${token('space.500', '40px')}`,
	paddingBottom: `${token('space.250', '20px')}`,
	paddingLeft: `${token('space.500', '40px')}`,
	// added in for cover image
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	'--full-cover-image-margin': ({ isCompanyHub }) => (isCompanyHub ? '-42px' : '-40px'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	'--full-cover-image-width': ({ isCompanyHub }) =>
		isCompanyHub ? 'calc(100% + 82px)' : 'calc(100% + 80px)',
});

export type SpaceOverviewProps = {
	spaceKey: string;
	children?: ReactNode;
};

const useFocusedCommentIdQuery = createSingleQueryParamHook('focusedCommentId');

export const SpaceOverview: FC<SpaceOverviewProps> = ({ spaceKey, children }) => {
	const { versionOverride } = useViewPageParams();
	const focusedCommentId = useFocusedCommentIdQuery();
	const { isSpaceTypesCreationFlowEnabled } = useCreateSpaceExperiment();
	const { isAdminHubAIEnabled, locale, isRovoEnabled } = useSessionData();
	const isReadingAidsAutoHighlightEnabled =
		(isAdminHubAIEnabled || isRovoEnabled) &&
		locale.startsWith('en') &&
		expVal('cc_reading_aids_auto-highlight_acronyms', 'isEnabled', false);

	const [contextContentId] = usePageContentId();
	const { shouldDisplaySidebar } = useContext(InlineCommentsContext);
	// @ts-ignore FIXME: `contextContentId` can be `undefined` here, and needs proper handling
	const contentId: string = contextContentId;

	// coverPictureWidth will be cleaned up once force-fixed width flag is cleaned up
	const { coverPictureWidth } = useTitleContentPropertiesForPublishedPage({
		contentId,
	});
	const isFixedWidth = coverPictureWidth === COVERPICTUREWIDTH.FIXED;

	const isCompanyHub = isCompanyHubSpaceKey(spaceKey);

	const isFixedWidthImageOption = !isCompanyHub && isFixedWidth;

	const spaceOverviewQueryResult = useQuery(
		// eslint-disable-next-line graphql-relay-compat/no-import-graphql-operations -- Read https://go/connie-relay-migration-fyi
		SpaceOverviewQuery,
		{
			variables: {
				spaceKey,
				excludeOperations: Boolean(!isCompanyHub),
			},
		},
	);
	const { data } = spaceOverviewQueryResult;
	const spaceName = data?.space?.name || '';
	const isThemed = data?.space?.theme?.themeKey || false;
	if (process.env.REACT_SSR && isThemed) {
		return null;
	}

	// The feature Company Hub uses a special space type "system" which is mostly
	// hidden from the UI and the user predominantly sees the space homepage /
	// overview presented with feature-specific customizations and referred to as
	// "front cover" or "landing page". Because the space is hidden from the user,
	// the UI concept "space icon" doesn't make sense.
	const spaceIconPath = isCompanyHub ? undefined : data?.space?.icon?.path;
	const emoji = getEmojiFromPublishedPageProperties(data?.space?.homepage);

	const isSpaceIconEnabled = isSpaceTypesCreationFlowEnabled && !!spaceIconPath;

	const ContentHeader = isCompanyHub ? CompanyHubGroupedButtons : SpaceOverviewGroupedButtons;
	const contentHeaderProps: ComponentProps<typeof CompanyHubGroupedButtons> &
		ComponentProps<typeof SpaceOverviewGroupedButtons> = {
		contentId,
		isThemed,
		spaceIconPath,
		spaceKey,
		spaceName,
		spaceOverviewQueryResult,
		versionOverride,
	};
	const contentHeader = <ContentHeader {...contentHeaderProps} />;

	return (
		<RendererActionsProvider>
			<>
				<StartSpaceOverviewPageLoad spaceKey={spaceKey} />
				<ViewPageContainer
					spaceKey={spaceKey}
					contentId={contentId}
					stickyHeader={contentHeader}
					isContainerContentView={!isCompanyHub}
					versionOverride={isCompanyHub ? versionOverride : null}
				>
					{({ contentQueryData, contentQueryError, isThemed }) => {
						const isFabricPage = Boolean(
							contentQueryData?.content?.nodes?.[0]?.body?.dynamic?.representation ===
								'atlas_doc_format',
						);

						const shouldRenderInlineComments = !isCompanyHub;

						const {
							coverPicture,
							contentAppearance,
							hasCoverPicture,
							coverPictureWidth,
							isTitleCenterAligned,
						} = getRendererCoverPictureInfo(contentQueryData);
						const isPageContentFullWidth = contentAppearance?.published === 'full-width';
						const hasEmoji = !!emoji || isSpaceIconEnabled;
						const isLeftAlignedTitleFullWidthPageWithFixedWidthImageAndEmoji =
							!isTitleCenterAligned &&
							isFixedWidthImageOption &&
							isPageContentFullWidth &&
							hasCoverPicture;

						return (
							<>
								{/* Body of content */}
								{/* Space Overview NVP non-theme uses StickyBanner inside of ViewPageContainer.
            Because StickyBanner is disabled when themes are enabled, need to display the
            header outside of content to match Space Overview Classic
            */}
								{isThemed && contentHeader}
								{!isCompanyHub && (
									<CustomHeaderFooterLoader type={CUSTOM_HEADER_FOOTER_TYPES.HEADER} />
								)}
								<ContentBodyStyledComponent
									isCompanyHub={isCompanyHub}
									data-testid="space-overview-content"
								>
									{hasCoverPicture && (
										<RendererPageCoverPicture
											contentId={contentId}
											isPagePreview={false}
											coverPicture={coverPicture}
											contentAppearance={contentAppearance}
											isSpaceOverview
											hasEmoji={hasEmoji}
											coverPictureWidthOverride={coverPictureWidth}
										/>
									)}
									<SpaceHeading
										{...{
											emoji,
											title: contentQueryData?.content?.nodes?.[0]?.title,
											isTitleCenterAligned,
											isCompanyHub,
											contentAppearance: contentAppearance?.published,
											hasCoverPicture,
											spaceIconPath,
											isLeftAlignedTitleFullWidthPageWithFixedWidthImageAndEmoji,
											isPageContentFullWidth,
											isFixedWidthImageOption,
										}}
									/>
									{shouldRenderInlineComments && (
										<InlineCommentsHighlighterLoader
											pageId={contentId}
											isFabricPage={isFabricPage}
										/>
									)}
									{isReadingAidsAutoHighlightEnabled && (
										<ReadingAidsAcronymsHighlighter contentId={contentId} />
									)}
									<DocumentUpdateStatusProvider>
										<ContentRefetchProvider>
											<AnnotationsProvider>
												<ViewPageContent
													contentId={contentId}
													data={contentQueryData}
													error={contentQueryError}
													spaceKey={spaceKey}
													hasInlineComments={shouldRenderInlineComments}
													isSpaceOverview
												/>
											</AnnotationsProvider>
										</ContentRefetchProvider>
									</DocumentUpdateStatusProvider>
									{!process.env.REACT_SSR && <ContentViewedEvent contentId={contentId} />}
									{!isCompanyHub && (
										<CustomHeaderFooterLoader type={CUSTOM_HEADER_FOOTER_TYPES.FOOTER} />
									)}
									<QuickReload
										contentId={contentId}
										contentType={contentQueryData?.content?.nodes?.[0]?.type}
										// The 'Date.now()' is expedient; see notes in quick-reload
										// package on what a more correct lastPollTime value would be.
										lastPollTime={Date.now()}
										pageMode={PageMode.RENDERER}
									/>
									{children}
								</ContentBodyStyledComponent>
								{shouldRenderInlineComments && shouldDisplaySidebar && (
									<InlineCommentsLoader
										pageId={contentId}
										isFabricPage={isFabricPage}
										loadingPriority={
											window.__SSR_RENDERED__ && focusedCommentId
												? LoadingPriority.AFTER_PAINT
												: LoadingPriority.BACKGROUND
										}
										focusedCommentId={focusedCommentId}
									/>
								)}
								{isCompanyHub && <ShareFeedbackButtonLoader />}
							</>
						);
					}}
				</ViewPageContainer>
			</>
		</RendererActionsProvider>
	);
};
